import { Fragment, useEffect, useMemo, useState } from "react";
import { AppHeader } from "@/components/AppHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { SearchableMultiSelect } from "@/components/ui/searchable-multi-select";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2,
  FileSpreadsheet,
  Calendar,
  Bug,
  RefreshCw,
  AlertTriangle,
  Info,
  XCircle,
  Timer,
  CircleDashed,
  RotateCcw,
  Eye,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  FolderOpen,
  ExternalLink,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useGroupedTestCases } from "@/hooks/useTestCases";
import { StatusBar } from "@/components/StatusBar";
import LoadingSpinner from "@/components/LoadingSpinner";
import { TesterSwitcher } from "@/components/TesterSwitcher";
import { getStatusConfig, normalizeStatus, type TestCaseStatus } from "@/lib/status-config";
import type { TesterUser } from "@/services/testers";
import { IssueDialog } from "@/components/IssueDialog";
import { IssueDetailDialog } from "@/components/IssueDetailDialog";
import type { TestCase, ExcelFileGroup, ProjectGroup, IssueSnapshot } from "@/types/domain";

// =============================================================================
// Status badge helper
// =============================================================================

const dueDateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

type DueFilter = "all" | "due" | "overdue";
type DueDateSort = "none" | "asc" | "desc";

function StatusBadge({ status }: { status: string }) {
  const normalized = normalizeStatus(status) as TestCaseStatus;
  switch (normalized) {
    case "ongoing":
      return (
        <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100">
          <Timer className="mr-1 h-3 w-3" /> In Progress
        </Badge>
      );
    case "new":
      return (
        <Badge className="bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100">
          <CircleDashed className="mr-1 h-3 w-3" /> New
        </Badge>
      );
    case "errors":
      return (
        <Badge className="bg-red-100 text-red-700 border-red-200 hover:bg-red-100">
          <AlertTriangle className="mr-1 h-3 w-3" /> Errors
        </Badge>
      );
    case "retest":
      return (
        <Badge className="bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-100">
          <RotateCcw className="mr-1 h-3 w-3" /> Retest
        </Badge>
      );
    case "ready_to_validate":
      return (
        <Badge className="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100">
          <Eye className="mr-1 h-3 w-3" /> In Queue
        </Badge>
      );
    case "closed":
      return (
        <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100">
          <CheckCircle2 className="mr-1 h-3 w-3" /> Closed
        </Badge>
      );
    default:
      return null;
  }
}

function parseDueDate(dueDate?: string): Date | null {
  if (!dueDate) return null;
  // Date-only strings ("YYYY-MM-DD") are parsed as UTC by the Date constructor,
  // causing off-by-one overdue checks in UTC+ timezones. Parse as local time.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dueDate);
  const parsed = dateOnly
    ? new Date(dueDate + "T00:00:00")
    : new Date(dueDate);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}


function formatDueDate(dueDate: string): string {
  const parsed = parseDueDate(dueDate);
  return parsed ? dueDateFormatter.format(parsed) : dueDate;
}

function isDueDateOverdue(dueDate?: string): boolean {
  const parsed = parseDueDate(dueDate);
  if (!parsed) return false;

  const dueDay = new Date(parsed);
  dueDay.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return dueDay < today;
}


// =============================================================================
// StepCard — one test step
// =============================================================================

interface StepCardProps {
  testCase: TestCase;
  stepIndex: number;
  totalVisible: number;
  isFinished?: boolean;
  onLogIssue: (tc: TestCase) => void;
  onViewIssue: (issue: IssueSnapshot) => void;
}

function StepCard({ testCase, isFinished = false, onLogIssue, onViewIssue }: StepCardProps) {
  const normalized = normalizeStatus(testCase.status) as TestCaseStatus;
  const isError = normalized === "errors";
  const isDone = normalized === "closed" || normalized === "ready_to_validate";
  const isOverdue = !isDone && isDueDateOverdue(testCase.due_date);
  const issue = testCase.issue;

  return (
    <div
      className={cn(
        "rounded-lg border px-4 py-3 space-y-2 transition-all",
        isFinished && "opacity-50 bg-muted/30",
        isError && "border-red-300 bg-red-50",
        !isFinished && !isError && "bg-card shadow-sm",
      )}
    >
      {/* Step header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          {/* Step number */}
          <span className="text-xs font-mono text-muted-foreground mt-0.5 shrink-0">
            #{testCase.step_number}
          </span>
          <p className="text-sm font-medium leading-snug">{testCase.description}</p>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <StatusBadge status={testCase.status} />
          {isOverdue && (
            <Badge className="bg-red-100 text-red-700 border-red-200 hover:bg-red-100">
              <AlertTriangle className="mr-1 h-3 w-3" /> Overdue
            </Badge>
          )}
        </div>
      </div>

      {isError && testCase.error_message && (
        <Alert className="border-red-200 bg-red-50 py-2 pl-3">
          <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
          <AlertDescription className="text-red-800 text-xs ml-1">
            {testCase.error_message}
          </AlertDescription>
        </Alert>
      )}

      {!isError && (
        <>
          {/* Expected result */}
          {testCase.expected_result && !isFinished && (
            <p className="text-xs text-muted-foreground pl-5">
              <span className="font-medium">Expected:</span> {testCase.expected_result}
            </p>
          )}

          {/* Metadata row */}
          {!isFinished && (
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground pl-5">
              {testCase.domain && <span>Tester: {testCase.domain}</span>}
              {testCase.area && <span>Area: {testCase.area}</span>}
              {testCase.transaction && <span>TX: {testCase.transaction}</span>}
              {testCase.due_date && (
                <span
                  className={cn(
                    "inline-flex items-center gap-1",
                    isOverdue && "text-destructive font-medium",
                  )}
                >
                  <Calendar className="h-3.5 w-3.5" />
                  Due: {formatDueDate(testCase.due_date)}
                </span>
              )}
            </div>
          )}
        </>
      )}

      {!issue && (
        <div className="flex justify-end pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onLogIssue(testCase)}
            className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50"
          >
            <Bug className="h-3.5 w-3.5" />
            Log Issue
          </Button>
        </div>
      )}

      {issue && (
        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={() => onViewIssue(issue)}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium bg-red-100 text-red-700 border border-red-200 hover:bg-red-200 transition-colors cursor-pointer"
          >
            <Bug className="h-3 w-3" />
            Issue Logged
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// ExcelFileSection — one Excel file / testscript within a project
// =============================================================================

interface ExcelFileSectionProps {
  group: ExcelFileGroup;
  showFuture: boolean;
  onLogIssue: (tc: TestCase) => void;
  onViewIssue: (issue: IssueSnapshot) => void;
}

function ExcelFileSection({ group, showFuture, onLogIssue, onViewIssue }: ExcelFileSectionProps) {
  const [finishedExpanded, setFinishedExpanded] = useState(false);
  const [futureExpanded, setFutureExpanded] = useState(false);

  const progressPct =
    group.allSteps.length > 0
      ? Math.round((group.finishedCount / group.allSteps.length) * 100)
      : 0;

  return (
    <div className="border rounded-xl overflow-hidden">
      {/* Excel file header */}
      <div
        className={cn(
          "flex items-center justify-between gap-3 px-4 py-3",
          group.isComplete
            ? "bg-green-50 border-b border-green-100"
            : group.hasError
              ? "bg-red-50 border-b border-red-100"
              : "bg-muted/40 border-b",
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <FileSpreadsheet
            className={cn(
              "h-4 w-4 shrink-0",
              group.isComplete
                ? "text-green-600"
                : group.hasError
                  ? "text-red-600"
                  : "text-muted-foreground",
            )}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-semibold truncate">{group.scenario_name}</p>
            </div>
            {group.testscript_id && (
              <p className="text-xs text-muted-foreground font-mono">{group.testscript_id}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {group.sharepoint_file_url && (
            <Button variant="outline" size="sm" asChild
              className="h-7 gap-1.5 px-2.5 text-xs font-medium text-primary border-primary/30 hover:bg-primary/10 hover:border-primary/50 shrink-0">
              <a href={group.sharepoint_file_url} target="_blank" rel="noopener noreferrer"
                 onClick={(e) => e.stopPropagation()}>
                <FileSpreadsheet className="h-3.5 w-3.5" />
                Open Testscript
                <ExternalLink className="h-3 w-3 opacity-50" />
              </a>
            </Button>
          )}
          {/* Mini progress */}
          <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
            <div className="w-20 bg-gray-200 rounded-full h-1.5 overflow-hidden">
              <div
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  group.isComplete ? "bg-green-500" : group.hasError ? "bg-red-500" : "bg-primary",
                )}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span>{progressPct}%</span>
          </div>

          {group.isComplete && (
            <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
              <CheckCircle2 className="mr-1 h-3 w-3" /> Done
            </Badge>
          )}
          {group.hasError && (
            <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
              <AlertTriangle className="mr-1 h-3 w-3" /> Blocked
            </Badge>
          )}

          <span className="text-xs text-muted-foreground">
            {group.finishedCount}/{group.allSteps.length}
          </span>
        </div>
      </div>

      {/* Steps */}
      <div className="p-3 space-y-2">
        {/* Finished steps — collapsed summary */}
        {group.finishedCount > 0 && (
          <Collapsible open={finishedExpanded} onOpenChange={setFinishedExpanded}>
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-left px-1 py-1 rounded hover:bg-muted/40">
                {finishedExpanded ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                <span>
                  {group.finishedCount} step{group.finishedCount !== 1 ? "s" : ""} completed
                </span>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-1.5 mt-1.5">
              {group.finishedSteps.map((tc) => (
                <StepCard
                  key={tc.id}
                  testCase={tc}
                  stepIndex={tc.step_number}
                  totalVisible={group.finishedSteps.length}
                  isFinished
                  onLogIssue={onLogIssue}
                  onViewIssue={onViewIssue}
                />
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Active step — the parent filter guarantees visibleSteps.length > 0 here */}
        <div className="space-y-2">
          {group.visibleSteps.map((tc) => (
            <StepCard
              key={tc.id}
              testCase={tc}
              stepIndex={tc.step_number}
              totalVisible={group.visibleSteps.length}
              onLogIssue={onLogIssue}
              onViewIssue={onViewIssue}
            />
          ))}
        </div>

        {/* Future steps — collapsible, only shown when toggle is on */}
        {showFuture && group.futureCount > 0 && (
          <Collapsible open={futureExpanded} onOpenChange={setFutureExpanded}>
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-left px-1 py-1 rounded hover:bg-muted/40">
                {futureExpanded ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
                <Calendar className="h-3.5 w-3.5 text-blue-500" />
                <span>
                  {group.futureCount} upcoming step{group.futureCount !== 1 ? "s" : ""}
                </span>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-1.5 mt-1.5">
              {group.futureSteps.map((tc) => (
                <StepCard
                  key={tc.id}
                  testCase={tc}
                  stepIndex={tc.step_number}
                  totalVisible={group.futureSteps.length}
                  isFinished
                  onLogIssue={onLogIssue}
                  onViewIssue={onViewIssue}
                />
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// ProjectSection — one project with its Excel file groups
// =============================================================================

interface ProjectSectionProps {
  project: ProjectGroup;
  showFuture: boolean;
  onLogIssue: (tc: TestCase) => void;
  onViewIssue: (issue: IssueSnapshot) => void;
}

function ProjectSection({ project, showFuture, onLogIssue, onViewIssue }: ProjectSectionProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [groupSearch, setGroupSearch] = useState("");
  const { stats } = project;

  const filteredGroups = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    if (!q) return project.excelGroups;
    return project.excelGroups.filter((group) => {
      if (group.scenario_name.toLowerCase().includes(q)) return true;
      if (group.testscript_id?.toLowerCase().includes(q)) return true;
      if (group.sharepoint_file_name?.toLowerCase().includes(q)) return true;
      return group.allSteps.some((step) => step.description?.toLowerCase().includes(q));
    });
  }, [project.excelGroups, groupSearch]);

  return (
    <Card className="overflow-hidden">
      {/* Project header — clickable to collapse */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="w-full text-left"
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-2 min-w-0">
              <FolderOpen className="h-5 w-5 text-primary shrink-0" />
              <CardTitle className="text-lg font-display truncate">{project.project_number ? `${project.project_number} - ` : ''}{project.project_name}</CardTitle>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {/* Aggregate badges */}
              {stats.errors > 0 && (
                <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                  <AlertTriangle className="mr-1 h-3 w-3" />
                  {stats.errors} error{stats.errors !== 1 ? "s" : ""}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">
                {stats.finished}/{stats.total} done
              </span>
              {/* Mini overall progress */}
              <div className="hidden md:flex items-center gap-2 text-xs text-muted-foreground">
                <div className="w-24 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-1.5 rounded-full bg-primary transition-all"
                    style={{ width: `${stats.progress_percentage}%` }}
                  />
                </div>
                <span>{stats.progress_percentage}%</span>
              </div>
              {collapsed ? (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </div>
        </CardHeader>
      </button>

      {!collapsed && (
        <CardContent className="pt-0 space-y-4 pb-4">
          {project.excelGroups.length > 1 && (
            <div className="relative max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search testscripts..."
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                className="pl-9 h-8 text-sm"
              />
            </div>
          )}

          {filteredGroups.length === 0 ? (
            <p className="text-sm text-center text-muted-foreground py-4">
              No testscripts matching &ldquo;{groupSearch.trim()}&rdquo;
            </p>
          ) : (
            filteredGroups.map((group) => (
              <ExcelFileSection key={group.groupKey} group={group} showFuture={showFuture} onLogIssue={onLogIssue} onViewIssue={onViewIssue} />
            ))
          )}
        </CardContent>
      )}
    </Card>
  );
}

// =============================================================================
// TesterDashboard — main page
// =============================================================================

const TesterDashboard = () => {
  const { user, isAdmin } = useAuth();
  const [selectedTester, setSelectedTester] = useState<TesterUser | null>(null);
  const [issueTestCase, setIssueTestCase] = useState<TestCase | null>(null);
  const [viewIssue, setViewIssue] = useState<IssueSnapshot | null>(null);
  const [dueFilter, setDueFilter] = useState<DueFilter>("all");
  const [showFuture, setShowFuture] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dueDateSort, setDueDateSort] = useState<DueDateSort>("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);

  const debouncedSearch = useDebouncedValue(searchQuery, 1800);

  // Reset page to 1 when any query param changes (tester switch, sort, filter, pageSize)
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTester?.id, dueDateSort, dueFilter, pageSize, selectedScenarios.join(","), debouncedSearch]);

  const {
    data: dashboardData,
    projectGroups,
    totalItems,
    totalPages,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGroupedTestCases({
    userId: selectedTester?.id,
    page: currentPage,
    pageSize,
    sort: dueDateSort,
    dueFilter: isAdmin ? dueFilter : undefined,
    view: "actionable",
    scenario: selectedScenarios.length > 0 ? selectedScenarios : undefined,
    search: debouncedSearch.trim() || undefined,
    refetchInterval: 300000,
  });

  const stats = dashboardData?.stats ?? {
    total: 0,
    new: 0,
    ongoing: 0,
    errors: 0,
    retest: 0,
    ready_to_validate: 0,
    closed: 0,
    blocked: 0,
    progress_percentage: 0,
  };

  const completionPercentage =
    stats.progress_percentage ??
    (stats.total > 0
      ? Math.round(((stats.ready_to_validate + stats.closed) / stats.total) * 100)
      : 0);

  const needsAction = stats.errors + stats.retest;
  const active = stats.ongoing;

  const dueFilterCounts = {
    due: dashboardData?.stats?.due_count ?? 0,
    overdue: dashboardData?.stats?.overdue_count ?? 0,
  };

  const totalFutureSteps = useMemo(() => {
    let count = 0;
    for (const project of projectGroups) {
      for (const group of project.excelGroups) {
        count += group.futureCount;
      }
    }
    return count;
  }, [projectGroups]);

  // Only render groups with an actionable step for this tester. Blocked-only
  // groups are dropped so the dashboard stays focused on work the tester can
  // act on now. The empty-page fallback below handles pages that filter to zero.
  const activeFilteredGroups = useMemo(() => {
    return projectGroups
      .map((project) => ({
        ...project,
        excelGroups: project.excelGroups.filter(
          (group) => group.visibleSteps.length > 0,
        ),
      }))
      .filter((project) => project.excelGroups.length > 0);
  }, [projectGroups]);

  // True empty state: server confirmed there are zero actionable items for this user.
  // Distinct from "current page has no renderable groups" — that is a pagination
  // navigation issue, not an empty-dataset issue.
  const isTrueEmpty = totalItems === 0;

  const scenarioOptions = useMemo(() => {
    return (dashboardData?.filter_options?.scenarios ?? []).map((s) => ({
      value: s.id,
      label: s.name,
    }));
  }, [dashboardData?.filter_options?.scenarios]);

  // Item pagination is server-side — totalPages comes from the API
  const safePage = Math.min(currentPage, totalPages || 1);

  // ==========================================================================
  // Loading state
  // ==========================================================================

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AppHeader title="My Tests" />
        <main className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <LoadingSpinner size="lg" />
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </main>
      </div>
    );
  }

  // ==========================================================================
  // Error state
  // ==========================================================================

  if (isError) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AppHeader title="My Tests" />
        <main className="flex-1 px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-destructive">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center gap-4 text-center">
                  <AlertTriangle className="h-12 w-12 text-destructive" />
                  <div>
                    <h2 className="text-lg font-semibold">Failed to load dashboard</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      {error instanceof Error ? error.message : "An unexpected error occurred"}
                    </p>
                  </div>
                  <Button onClick={() => refetch()} className="gap-2">
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  // ==========================================================================
  // Main view
  // ==========================================================================

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader title="My Tests" />

      <main className="flex-1 px-6 py-8">
        <div className="max-w-4xl mx-auto space-y-8">

          {/* Welcome section */}
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground">
                {selectedTester
                  ? `${selectedTester.name}'s Tests`
                  : `Welcome back${user?.name ? `, ${user.name.split(" ")[0]}` : ""}!`}
              </h1>
              <p className="text-muted-foreground mt-1">
                {selectedTester
                  ? `Viewing test cases assigned to ${selectedTester.name}`
                  : "Here's an overview of your assigned test cases"}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <TesterSwitcher
                selectedTesterId={selectedTester?.id}
                onSelect={(tester) => setSelectedTester(tester)}
              />
              <Button
                variant="outline"
                onClick={() => refetch()}
                disabled={isFetching}
                className="gap-2"
              >
                <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
                Refresh
              </Button>
            </div>
          </div>

          {/* SharePoint sync info */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Test execution happens in SharePoint. Status updates sync automatically from the Excel files.
            </AlertDescription>
          </Alert>

          {/* Stats cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <FileSpreadsheet className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total}</p>
                    <p className="text-sm text-muted-foreground">Total Steps</p>
                    {(() => {
                      const actionable =
                        stats.total - (stats.closed ?? 0) - (stats.ready_to_validate ?? 0);
                      return actionable > 0 && actionable !== stats.total ? (
                        <p className="text-xs text-muted-foreground/80">
                          {actionable} actionable
                        </p>
                      ) : null;
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.closed}</p>
                    <p className="text-sm text-muted-foreground">Closed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                    <XCircle className="h-5 w-5 text-destructive" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{needsAction}</p>
                    <p className="text-sm text-muted-foreground">Needs Action</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-info/10 flex items-center justify-center">
                    <Timer className="h-5 w-5 text-info" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{active}</p>
                    <p className="text-sm text-muted-foreground">Active</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Status breakdown strip */}
          <div className="flex flex-wrap gap-2">
            {(["new", "ongoing", "errors", "retest", "ready_to_validate", "closed"] as TestCaseStatus[]).map(
              (s) => {
                const config = getStatusConfig(s);
                const count = stats[s] ?? 0;
                return (
                  <span
                    key={s}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
                      config.badgeClasses,
                    )}
                  >
                    {config.label}: {count}
                  </span>
                );
              },
            )}
          </div>

          {/* Overall progress */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display">Overall Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Completion</span>
                  <span className="font-medium">{completionPercentage}%</span>
                </div>
                <StatusBar
                  counts={stats as Record<TestCaseStatus, number>}
                  total={stats.total}
                  height="h-3"
                />
                <p className="text-sm text-muted-foreground">
                  {stats.ready_to_validate + stats.closed} of {stats.total} steps done
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Hierarchical test view */}
          <div>
            <div className="mb-4">
              <h2 className="text-2xl font-display font-semibold">Your Tests</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Grouped by project and Excel file — status synced from SharePoint
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mt-3">
                <div className="relative max-w-sm flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search projects, scenarios, steps..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                {scenarioOptions.length > 1 && (
                  <SearchableMultiSelect
                    selected={selectedScenarios}
                    onToggle={(value) => {
                      setSelectedScenarios((prev) =>
                        prev.includes(value)
                          ? prev.filter((v) => v !== value)
                          : [...prev, value],
                      );
                    }}
                    options={scenarioOptions}
                    placeholder="All Scenarios"
                    searchPlaceholder="Search scenarios..."
                    className="w-[220px]"
                  />
                )}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {totalFutureSteps > 0 && (
                  <Button
                    size="sm"
                    variant={showFuture ? "default" : "outline"}
                    onClick={() => setShowFuture(!showFuture)}
                    className="gap-1.5"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    {showFuture ? "Hide" : "Show"} future steps ({totalFutureSteps})
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={dueDateSort !== "none" ? "default" : "outline"}
                  onClick={() =>
                    setDueDateSort((prev) =>
                      prev === "none" ? "asc" : prev === "asc" ? "desc" : "none",
                    )
                  }
                  className="gap-1.5"
                >
                  {dueDateSort === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5" />
                  ) : dueDateSort === "desc" ? (
                    <ArrowDown className="h-3.5 w-3.5" />
                  ) : (
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  )}
                  Due Date
                  {dueDateSort === "asc" && " ↑"}
                  {dueDateSort === "desc" && " ↓"}
                </Button>
                {isAdmin && (
                  <>
                    <span className="text-xs text-muted-foreground">Filter:</span>
                    <Button
                      size="sm"
                      variant={dueFilter === "all" ? "default" : "outline"}
                      onClick={() => setDueFilter("all")}
                    >
                      All
                    </Button>
                    <Button
                      size="sm"
                      variant={dueFilter === "due" ? "default" : "outline"}
                      onClick={() => setDueFilter("due")}
                    >
                      Due ({dueFilterCounts.due})
                    </Button>
                    <Button
                      size="sm"
                      variant={dueFilter === "overdue" ? "default" : "outline"}
                      onClick={() => setDueFilter("overdue")}
                    >
                      Overdue ({dueFilterCounts.overdue})
                    </Button>
                  </>
                )}
              </div>
            </div>

            {isTrueEmpty ? (
              <div className="text-center py-12 text-muted-foreground">
                {searchQuery.trim()
                  ? `No projects matching "${searchQuery.trim()}".`
                  : isAdmin && dueFilter !== "all"
                    ? `No ${dueFilter} steps found for the selected tester.`
                    : "No test cases assigned to you yet."}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {isFetching
                      ? "Loading…"
                      : totalPages > 1
                        ? `Showing ${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, totalItems)} of ${totalItems} steps`
                        : `${totalItems} step${totalItems !== 1 ? "s" : ""}`}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Per page:</span>
                    <Select
                      value={String(pageSize)}
                      onValueChange={(val) => setPageSize(Number(val))}
                    >
                      <SelectTrigger className="w-[70px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {isFetching ? (
                  <div className="flex items-center justify-center py-24">
                    <div className="flex flex-col items-center gap-3">
                      <LoadingSpinner size="lg" />
                      <p className="text-muted-foreground">Loading test cases...</p>
                    </div>
                  </div>
                ) : activeFilteredGroups.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    {searchQuery.trim()
                      ? `No projects matching "${searchQuery.trim()}" on this page.`
                      : "No items on this page — use the pagination controls below to navigate."}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {activeFilteredGroups.map((project) => (
                      <ProjectSection
                        key={project.project_id}
                        project={project}
                        showFuture={showFuture}
                        onLogIssue={(tc) => setIssueTestCase(tc)}
                        onViewIssue={(issue) => setViewIssue(issue)}
                      />
                    ))}
                  </div>
                )}

                {totalPages > 1 && (
                  <Pagination className="mt-6">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          aria-disabled={safePage <= 1 || undefined}
                          tabIndex={safePage <= 1 ? -1 : undefined}
                          className={cn(safePage <= 1 && "pointer-events-none opacity-50")}
                        />
                      </PaginationItem>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                        .map((p, idx, arr) => (
                          <Fragment key={p}>
                            {idx > 0 && arr[idx - 1] !== p - 1 && (
                              <PaginationItem><PaginationEllipsis /></PaginationItem>
                            )}
                            <PaginationItem>
                              <PaginationLink
                                isActive={p === safePage}
                                onClick={() => setCurrentPage(p)}
                              >
                                {p}
                              </PaginationLink>
                            </PaginationItem>
                          </Fragment>
                        ))}
                      <PaginationItem>
                        <PaginationNext
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          aria-disabled={safePage >= totalPages || undefined}
                          tabIndex={safePage >= totalPages ? -1 : undefined}
                          className={cn(safePage >= totalPages && "pointer-events-none opacity-50")}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <footer className="w-full py-4 px-6 border-t border-border">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Lotus Bakeries. Internal Testing Portal.
          </p>
        </div>
      </footer>

      {/* Issue create dialog */}
      {issueTestCase && (
        <IssueDialog
          testCase={issueTestCase}
          open={!!issueTestCase}
          onOpenChange={(open) => !open && setIssueTestCase(null)}
          onIssueCreated={() => {
            setIssueTestCase(null);
            refetch();
          }}
        />
      )}

      {/* Issue detail dialog */}
      {viewIssue && (
        <IssueDetailDialog
          issue={viewIssue}
          open={!!viewIssue}
          onOpenChange={(open) => !open && setViewIssue(null)}
        />
      )}
    </div>
  );
};

export default TesterDashboard;
